import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import Chart from 'react-apexcharts';
import { get } from '../../controllers/apiController';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/format';

const YoneticiPaneli = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [genelOzet, setGenelOzet] = useState({});
    const [pipelineDagilimi, setPipelineDagilimi] = useState([]);
    const [yaklasanKapanislar, setYaklasanKapanislar] = useState([]);
    const [gecikenFirsatlar, setGecikenFirsatlar] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Yönetici Paneli | LEVELUP CRM";
        const fetchAll = async () => {
            try {
                const res = await get('reports/manager-panel');
                setGenelOzet(res?.genelOzet || {});
                setPipelineDagilimi(res?.pipelineDagilimi || []);
                setYaklasanKapanislar(res?.yaklasanKapanislar || []);
                setGecikenFirsatlar(res?.gecikenFirsatlar || []);
            } catch {
                toast.error("Veriler alınırken bir hata oluştu!");
            }
            setIsLoading(false);
        };
        fetchAll();
    }, []);

    const formatDate = (d) => {
        if (!d) return '—';
        const dt = new Date(d);
        return `${dt.getDate().toString().padStart(2, '0')}.${(dt.getMonth() + 1).toString().padStart(2, '0')}.${dt.getFullYear()}`;
    };

    const KPI_ITEMS = [
        { icon: 'briefcase', color: '#3b82f6', value: Number(genelOzet.acik_firsat_sayisi || 0), label: 'Toplam Açık Fırsat' },
        { icon: 'dollar-sign', color: '#8b5cf6', value: formatCurrency(genelOzet.toplam_teklif_tutari), label: 'Toplam Teklif Tutarı' },
        { icon: 'trending-up', color: '#6366f1', value: formatCurrency(genelOzet.toplam_agirlikli_ciro), label: 'Toplam Ağırlıklı Ciro' },
        { icon: 'check-circle', color: '#10b981', value: Number(genelOzet.bu_ay_kazanilan || 0), label: 'Bu Ay Kazanılan' },
        { icon: 'x-circle', color: '#ef4444', value: Number(genelOzet.bu_ay_kaybedilen || 0), label: 'Bu Ay Kaybedilen' },
    ];

    const pipelineOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
        colors: pipelineDagilimi.map(s => s.Renk || '#6366f1'),
        legend: { show: false },
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
        xaxis: { categories: pipelineDagilimi.map(s => s.Ad), labels: { style: { fontSize: '11px' } } },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: { y: { formatter: v => v + ' fırsat' } }
    };
    const pipelineSeries = [{ name: 'Fırsat Sayısı', data: pipelineDagilimi.map(s => Number(s.firsat_sayisi)) }];

    const renderFirsatTable = (rows, emptyText, danger = false) => (
        rows.length === 0 ? (
            <div className="text-center py-5 text-muted">
                <i className={`feather-${danger ? 'check-circle text-success' : 'inbox'} fs-1 d-block mb-2`}></i>
                <p className="small mb-0">{emptyText}</p>
            </div>
        ) : (
            <div className="table-responsive">
                <table className="table table-hover mb-0">
                    <thead>
                        <tr>
                            <th style={{ fontSize: 12 }}>Firma Adı</th>
                            <th style={{ fontSize: 12 }}>Lead Kaynağı</th>
                            <th style={{ fontSize: 12 }}>Aylık Teklif Tutarı</th>
                            <th style={{ fontSize: 12 }}>Kapanma İhtimali</th>
                            <th style={{ fontSize: 12 }}>Beklenen Kapanış Tarihi</th>
                            <th style={{ fontSize: 12 }}>Satış Sorumlusu</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}
                                onClick={() => navigate(`/lead-detail?id=${row.Form_ID}`)}
                                style={{ cursor: 'pointer' }}
                                className={danger ? 'table-danger' : ''}>
                                <td style={{ fontSize: 13 }}>
                                    {danger && <i className="feather-alert-circle text-danger me-1" style={{ fontSize: 11 }}></i>}
                                    {row.Firma_Adi || '—'}
                                </td>
                                <td style={{ fontSize: 13 }}>{row.Kaynak || '—'}</td>
                                <td style={{ fontSize: 13 }}>{formatCurrency(row.Teklif_Tutari)}</td>
                                <td style={{ fontSize: 13 }}>%{row.Kapanma_Yuzdesi ?? 0}</td>
                                <td style={{ fontSize: 13 }}>{formatDate(row.Beklenen_Kapanis_Tarihi)}</td>
                                <td style={{ fontSize: 13 }}>{row.Satis_Sorumlusu?.trim() || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    );

    return (
        <Layout>
            <main className="nxl-container">
                <div className="nxl-content">
                    <div className="page-header">
                        <div className="page-header-left d-flex align-items-center">
                            <div className="page-header-title">
                                <h5 className="m-b-10">Yönetici Paneli</h5>
                            </div>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item"><a href="./">Anasayfa</a></li>
                                <li className="breadcrumb-item">Yönetici Paneli</li>
                            </ul>
                        </div>
                    </div>

                    <div className="main-content">
                        {isLoading ? (
                            <div className="w-100 d-flex align-items-center justify-content-center" style={{ height: '70svh' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Yükleniyor...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Genel Satış Özeti */}
                                <div className="row g-3 mb-4">
                                    {KPI_ITEMS.map((k, i) => (
                                        <div key={i} className="col-6 col-md-4 col-xl">
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body d-flex align-items-center gap-3 py-4">
                                                    <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                                        style={{ width: 48, height: 48, background: k.color + '18', color: k.color }}>
                                                        <i className={`feather-${k.icon} fs-5`}></i>
                                                    </div>
                                                    <div className="overflow-hidden">
                                                        <h5 className="fw-bold mb-0 text-truncate" style={{ color: k.color }}>{k.value}</h5>
                                                        <p className="text-muted mb-0 small">{k.label}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pipeline Dağılımı */}
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-bottom py-3">
                                        <h6 className="mb-0 fw-semibold">
                                            <i className="feather-git-branch me-2 text-primary"></i>Pipeline Dağılımı
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        {pipelineDagilimi.length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <i className="feather-inbox fs-1 d-block mb-2"></i>
                                                <p className="small mb-0">Pipeline aşaması bulunamadı</p>
                                            </div>
                                        ) : (
                                            <>
                                                <Chart options={pipelineOptions} series={pipelineSeries} type="bar" height={Math.max(240, pipelineDagilimi.length * 40)} />
                                                <div className="table-responsive mt-3">
                                                    <table className="table table-sm mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ fontSize: 12 }}>Aşama</th>
                                                                <th style={{ fontSize: 12 }}>Fırsat Sayısı</th>
                                                                <th style={{ fontSize: 12 }}>Toplam Teklif Tutarı</th>
                                                                <th style={{ fontSize: 12 }}>Toplam Ağırlıklı Ciro</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {pipelineDagilimi.map(s => (
                                                                <tr key={s.Asama_ID}>
                                                                    <td style={{ fontSize: 13 }}>
                                                                        <span className="badge me-1" style={{ background: (s.Renk || '#6366f1') + '20', color: s.Renk || '#6366f1' }}>
                                                                            {s.Ad}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ fontSize: 13 }}>{Number(s.firsat_sayisi)}</td>
                                                                    <td style={{ fontSize: 13 }}>{formatCurrency(s.toplam_teklif_tutari)}</td>
                                                                    <td style={{ fontSize: 13 }}>{formatCurrency(s.toplam_agirlikli_ciro)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Yaklaşan Kapanışlar */}
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                                        <h6 className="mb-0 fw-semibold">
                                            <i className="feather-calendar me-2 text-primary"></i>
                                            Yaklaşan Kapanışlar
                                            <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(30 Gün)</span>
                                        </h6>
                                        {yaklasanKapanislar.length > 0 && (
                                            <span className="badge bg-primary">{yaklasanKapanislar.length}</span>
                                        )}
                                    </div>
                                    <div className="card-body p-0">
                                        {renderFirsatTable(yaklasanKapanislar, 'Önümüzdeki 30 günde kapanması beklenen fırsat yok')}
                                    </div>
                                </div>

                                {/* Geciken Fırsatlar */}
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                                        <h6 className="mb-0 fw-semibold">
                                            <i className="feather-alert-triangle me-2 text-danger"></i>
                                            Geciken Fırsatlar
                                        </h6>
                                        {gecikenFirsatlar.length > 0 && (
                                            <span className="badge bg-danger">{gecikenFirsatlar.length}</span>
                                        )}
                                    </div>
                                    <div className="card-body p-0">
                                        {renderFirsatTable(gecikenFirsatlar, 'Geciken fırsat yok', true)}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>
        </Layout>
    );
};

export default YoneticiPaneli;
